import * as XLSX from 'xlsx-js-style'
import { processSubActivities, applySubActivityStyles } from './subActivityUtilities'
import { getActivityExportSubHeaders } from 'containers/VerticalPage/nft/Activity/utils'
import { calculateColumnWidths } from './downloadCSV'

interface SheetData {
  name: string
  headers: string[]
  data: any[]
  isTransposed: boolean
  isSellerRegistry: boolean
}

interface ExportData {
  entityId: string
  verticalType: string
  sheets: SheetData[]
}

const transposeData = (data: any[][]) => {
  if (!Array.isArray(data) || data.length === 0) return []
  return data[0].map((_, colIndex) => data.map(row => row[colIndex]))
}

const formatDataForExcel = (data: any[]) => {
  if (!Array.isArray(data)) return []

  if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object') {
    return data.map(row => Object.values(row))
  }

  return Array.isArray(data[0]) ? data : [data]
}

const formatNumericValue = (value: any) => {
  if (typeof value === 'number') {
    return value
  }

  if (typeof value === 'string' && /^-?\d*\.?\d+$/.test(value.trim())) {
    const numValue = Number(value)
    return numValue
  }

  return value
}

const needsCryptoActivityStyling = (sheet: SheetData, verticalType: string) => {
  return verticalType === 'crypto' && sheet.name.toLowerCase().includes('activity')
}

export const generateExcelFromData = (
  exportData: ExportData,
  fileName: string,
  currencyContext?: { currencyConversion: boolean; currentCurrency: string; exchangeRates?: any },
) => {
  try {
    const workbook = XLSX.utils.book_new()

    for (const sheet of exportData.sheets) {
      let worksheet: XLSX.WorkSheet

      if (needsCryptoActivityStyling(sheet, exportData.verticalType)) {
        const objectData = sheet.data
        const dynamicSubHeaders = getActivityExportSubHeaders(
          currencyContext || {
            currencyConversion: false,
            currentCurrency: 'USD',
          },
        )

        const { rows, levels } = processSubActivities(objectData, dynamicSubHeaders)

        worksheet = XLSX.utils.json_to_sheet([])

        const filteredHeaders = sheet.headers.filter(header => header !== 'Sub Activities')
        XLSX.utils.sheet_add_aoa(worksheet, [filteredHeaders])
        XLSX.utils.sheet_add_json(worksheet, rows, { skipHeader: true, origin: -1 })

        const columnWidths = calculateColumnWidths(filteredHeaders)
        worksheet['!cols'] = columnWidths

        const range = XLSX.utils.decode_range(worksheet['!ref']!)
        worksheet['!rows'] = levels.map(level => ({
          hidden: false,
          level: level.level,
        }))

        applySubActivityStyles(worksheet, range, rows, levels, dynamicSubHeaders)
      } else {
        let sheetData

        if (sheet.isSellerRegistry) {
          sheetData = formatDataForExcel(sheet.data)
        } else {
          sheetData = [sheet.headers, ...formatDataForExcel(sheet.data)]
        }

        sheetData = sheetData.map((row, rowIndex) => {
          if (rowIndex === 0) return row // Skip headers
          return row.map((cell: any) => formatNumericValue(cell))
        })

        if (sheet.isTransposed) {
          sheetData = transposeData(sheetData)
        }

        worksheet = XLSX.utils.aoa_to_sheet(sheetData)
      }

      XLSX.utils.book_append_sheet(workbook, worksheet, sheet.name)
    }

    const writeOptions = {
      compression: true,
    }
    XLSX.writeFile(workbook, `${fileName}.xlsx`, writeOptions)
  } catch (error) {
    console.error('Error generating Excel file:', error)
    throw new Error('Failed to generate Excel file')
  }
}
